import { useEffect, useRef } from 'react';

/** Dispatched on soft refresh; listeners re-fetch page data. */
export const APP_AUTO_REFRESH_EVENT = 'risklo:auto-refresh';

const THIRTY_MIN_MS = 30 * 60 * 1000;
const FIVE_MIN_MS = 5 * 60 * 1000;
const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;

/**
 * Keeps long-lived tabs from showing stale data.
 * - Every `intervalMs` while the tab is visible: soft refresh (custom event + optional callback).
 * - After the tab was hidden ≥ `hiddenResumeMs`: refresh when the user returns.
 * - After `hardReloadMs` since mount (tab visible): full page reload (new deploys + full reset).
 */
export function useAppAutoRefresh({
  intervalMs = THIRTY_MIN_MS,
  hiddenResumeMs = FIVE_MIN_MS,
  hardReloadMs = EIGHT_HOURS_MS,
  onRefresh,
} = {}) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const openedAt = Date.now();
    let hiddenAt = null;

    const fireRefresh = (reason) => {
      if (typeof onRefreshRef.current === 'function') onRefreshRef.current(reason);
      window.dispatchEvent(new CustomEvent(APP_AUTO_REFRESH_EVENT, { detail: { reason } }));
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt != null && Date.now() - hiddenAt >= hiddenResumeMs) {
        fireRefresh('visibility');
      }
      hiddenAt = null;
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (hardReloadMs > 0 && Date.now() - openedAt >= hardReloadMs) {
        window.location.reload();
        return;
      }
      fireRefresh('interval');
    }, intervalMs);

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, hiddenResumeMs, hardReloadMs]);
}

/** Subscribe to global soft refresh. Returns unsubscribe. */
export function subscribeAppAutoRefresh(handler) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(APP_AUTO_REFRESH_EVENT, handler);
  return () => window.removeEventListener(APP_AUTO_REFRESH_EVENT, handler);
}
